import { createDragDropManager } from "dnd-core";
import { memo, ReactNode } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

/**
 * Create the manager once at module load time so there is exactly one backend
 * and one set of DOM listeners regardless of how many times this component
 * mounts or unmounts. This avoids "Cannot have two HTML5 backends at the same
 * time" errors that occur when error boundaries skip DndProvider teardown,
 * and prevents double-listener leaks from that same scenario.
 *
 * The HTML5Backend sets a `__isReactDndBackendSetUp` flag on `window` during setup
 * and clears it during teardown. If teardown is skipped (e.g. due to an error boundary
 * catching an unrelated error), the flag persists and the next DndProvider mount
 * throws "Cannot have two HTML5 backends at the same time."
 *
 * Since this is the only place we mount DndProvider, we clear the orphaned flag
 * before each mount so setup can proceed cleanly.
 */
const manager = createDragDropManager(HTML5Backend);

export const Html5DndProviderSingleton = memo(
  function Html5DndProviderSingleton({ children }: { children: ReactNode }) {
    (
      window as { __isReactDndBackendSetUp?: boolean }
    ).__isReactDndBackendSetUp = false;
    return <DndProvider manager={manager}>{children}</DndProvider>;
  },
);
